import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	IconButton,
	Typography,
	Chip,
	Tooltip,
	Skeleton
} from '@mui/material';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import { useGetAddressesListQuery } from '@/store/services/address';
import { useNavigate } from 'react-router-dom';

type Address = {
	id: number;
	user_id: number;
	name: string | null;
	location_name: string | null;
	is_enabled: number;
	type: string | null;
	user: {
		id: number;
		name: string;
		email: string;
	} | null;
	documents: unknown[];
	application: unknown | null;
};

type AddressesTableProps = {
	onEdit: (address: Address) => void;
};

function AddressesTable({ onEdit }: AddressesTableProps) {
	const navigate = useNavigate();
	const { data: addressesResponse, isLoading, isFetching } = useGetAddressesListQuery({});

	const addresses = addressesResponse?.data || [];

	const getTypeLabel = (type: string | null) => {
		if (!type) return 'N/A';

		return type.charAt(0).toUpperCase() + type.slice(1);
	};

	const getTypeColor = (type: string | null) => {
		if (!type) return 'default';

		const typeMap: Record<string, 'primary' | 'success' | 'warning'> = {
			apartment: 'primary',
			house: 'success',
			office: 'warning'
		};

		return typeMap[type] || 'default';
	};

	const showSkeleton = isLoading || isFetching;

	return (
		<Paper
			className="flex flex-col flex-auto shadow-1 rounded-lg overflow-hidden mb-4"
			elevation={0}
		>
			<TableContainer>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>
								<Typography className="font-semibold">Name</Typography>
							</TableCell>
							<TableCell>
								<Typography className="font-semibold">Address</Typography>
							</TableCell>
							<TableCell>
								<Typography className="font-semibold">Type</Typography>
							</TableCell>
							<TableCell>
								<Typography className="font-semibold">Documents</Typography>
							</TableCell>
							<TableCell align="right">
								<Typography className="font-semibold">Actions</Typography>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{showSkeleton ? (
							Array.from({ length: 2 }).map((_, i) => (
								<TableRow key={i}>
									{Array.from({ length: 5 }).map((_, colIdx) => (
										<TableCell key={colIdx}>
											<Skeleton variant="rounded" width={colIdx === 0 ? 140 : 90} height={24} animation="wave" />
										</TableCell>
									))}
								</TableRow>
							))
						) : addresses.length === 0 ? (
							<TableRow>
								<TableCell
									colSpan={5}
									align="center"
								>
									<Typography
										variant="body2"
										className="text-gray-500 py-8"
									>
										No addresses found. Add your first address using the form above.
									</Typography>
								</TableCell>
							</TableRow>
						) : (
							addresses.map((address: Address) => {
								const isDisabled = address.is_enabled === 0;
								const hasDocuments = (address.documents?.length || 0) > 0;
								const hasApplicationOrAsset = address.application !== null;
								const disableEdit = isDisabled || hasDocuments || hasApplicationOrAsset;

								const editTooltip = isDisabled
									? 'This address is disabled'
									: hasApplicationOrAsset
										? 'This address is linked to an application/asset'
										: hasDocuments
											? 'This address has documents attached'
											: 'Edit Address';

								return (
								<TableRow
									key={address.id}
									hover
									sx={{
										opacity: isDisabled ? 0.6 : 1,
										cursor: isDisabled ? 'not-allowed' : 'default'
									}}
								>
									<TableCell>
										<div className="flex items-center gap-8">
											<Typography>{address.name || 'N/A'}</Typography>
											<div className="flex items-center gap-2">
												{isDisabled && (
													<Chip
														label="Disabled"
														size="small"
														variant="outlined"
														color="default"
													/>
												)}
												{hasApplicationOrAsset && (
													<Chip
														label="In Use"
														size="small"
														variant="outlined"
														color="default"
													/>
												)}

											</div>
										</div>
									</TableCell>
									<TableCell>
										<Typography>{address.location_name || 'N/A'}</Typography>
									</TableCell>
									<TableCell>
										<Chip
											label={getTypeLabel(address.type)}
											color={getTypeColor(address.type)}
											size="small"
										/>
									</TableCell>
									<TableCell>
										<Tooltip
											title={
												hasDocuments
													? isDisabled
														? 'Address is disabled'
														: 'View Documents'
													: 'No Documents'
											}
											arrow
											placement="top"
										>
											<Typography
												onClick={() => {
													if (!isDisabled && hasDocuments) {
														navigate('/apps/documents');
													}
												}}
												sx={{
													display: 'inline-block',
													cursor:
														!isDisabled && hasDocuments ? 'pointer' : 'default',
													color: !isDisabled && hasDocuments ? 'primary.main' : 'inherit',
													textDecoration:
														!isDisabled && hasDocuments ? 'underline' : 'none',
													'&:hover': {
														textDecoration:
															!isDisabled && hasDocuments ? 'underline' : 'none'
													}
												}}
											>
												{address.documents?.length || 0}
											</Typography>
										</Tooltip>
									</TableCell>
									<TableCell align="right">
										<Tooltip
											title={editTooltip}
											arrow
											placement="top"
										>
											<span>
												<IconButton
													onClick={() => onEdit(address)}
													size="small"
													color="primary"
													disabled={disableEdit}
												>
													<FuseSvgIcon>heroicons-outline:pencil-square</FuseSvgIcon>
												</IconButton>
											</span>
										</Tooltip>
									</TableCell>
								</TableRow>
								);
							})
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</Paper>
	);
}

export default AddressesTable;
